import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

/**
 * Extract items from a paginated API response.
 * Handles both new format {items, total, page, ...} and legacy flat arrays.
 */
export const extractItems = (response) => {
  const data = response?.data;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.items && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Extract pagination metadata from a paginated response.
 */
export const extractPagination = (response) => {
  const data = response?.data;
  if (!data || Array.isArray(data)) return { total: Array.isArray(data) ? data.length : 0, page: 1, pages: 1, limit: 50 };
  return { total: data.total || 0, page: data.page || 1, pages: data.pages || 1, limit: data.limit || 50 };
};

// Legacy token cleanup — only used to clear old sessionStorage keys on logout
const TOKEN_KEY = 'simlabdt_token';

export const clearToken = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {}
};

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Send httpOnly cookies on every request
  timeout: 120000, // 2 minutes timeout for AI operations
});

// ─── CSRF helpers ───
const CSRF_PROTECTED_METHODS = new Set(['post', 'put', 'patch', 'delete']);
let csrfTokenInMemory = null;

export const setCsrfToken = (token) => { csrfTokenInMemory = token; };

const getCsrfToken = () => {
  // Prefer in-memory token (always latest from response header)
  if (csrfTokenInMemory) return csrfTokenInMemory;
  // Fallback to cookie
  try {
    const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
    if (match) { csrfTokenInMemory = decodeURIComponent(match[1]); return csrfTokenInMemory; }
  } catch {}
  return null;
};

const storeCsrfFromResponse = (response) => {
  const headerToken = response?.headers?.['x-csrf-token'];
  if (headerToken) csrfTokenInMemory = headerToken;
};

const refreshCsrfToken = async () => {
  try { const r = await axios.get(`${API_BASE}/health`, { withCredentials: true }); storeCsrfFromResponse(r); } catch {}
};

api.interceptors.request.use(
  async (config) => {
    if (CSRF_PROTECTED_METHODS.has((config.method || '').toLowerCase())) {
      const token = getCsrfToken();
      if (token) config.headers['X-CSRF-Token'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Token refresh logic
let isRefreshing = false;
let failedQueue = [];
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 2;

const processQueue = (error) => {
  failedQueue.forEach(prom => { if (error) prom.reject(error); else prom.resolve(); });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => { storeCsrfFromResponse(response); refreshAttempts = 0; return response; },
  async (err) => {
    const originalRequest = err.config;

    // CSRF self-healing: read fresh token from 403 response header (matches cookie)
    if (
      err.response?.status === 403 &&
      err.response?.data?.detail?.includes('CSRF') &&
      !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;
      const newToken = err.response?.headers?.['x-csrf-token'] || err.response?.data?.csrf_token;
      if (newToken) {
        csrfTokenInMemory = newToken;
        originalRequest.headers['X-CSRF-Token'] = newToken;
        return api(originalRequest);
      }
    }

    // Only handle 401 errors below
    if (err.response?.status !== 401) {
      return Promise.reject(err);
    }
    
    // Don't retry auth endpoints - just reject
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/me'];
    if (authEndpoints.some(ep => originalRequest.url?.includes(ep))) {
      return Promise.reject(err);
    }
    
    // Don't retry if already retried
    if (originalRequest._retry) {
      return Promise.reject(err);
    }
    
    // Prevent infinite refresh loops
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.log('Max refresh attempts reached, redirecting to login');
      clearToken();
      try {
        sessionStorage.removeItem('simlabdt_user');
        localStorage.removeItem('simlabdt_user');
      } catch {}
      refreshAttempts = 0;
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
      return Promise.reject(new Error('Session expired. Please sign in again.'));
    }
    
    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          // Cookie was refreshed server-side, just retry with credentials
          return api(originalRequest);
        })
        .catch(err => Promise.reject(err));
    }
    
    // Start refresh
    originalRequest._retry = true;
    isRefreshing = true;
    refreshAttempts++;
    
    try {
      console.log(`Attempting token refresh (attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS})...`);

      // Server sets new access_token cookie on successful refresh
      await api.post('/auth/refresh');
      console.log('Token refresh successful');

      // Process queued requests — cookie is now updated server-side
      processQueue(null);
      refreshAttempts = 0; // Reset on success

      // Retry original request (cookie will be sent automatically)
      return api(originalRequest);
      
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError.response?.data?.detail || refreshError.message);
      
      // Process queued requests with error
      processQueue(refreshError);
      
      // Clear auth state
      clearToken();
      try {
        sessionStorage.removeItem('simlabdt_user');
        localStorage.removeItem('simlabdt_user');
      } catch {}
      
      // Redirect to login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
      
      return Promise.reject(new Error('Session expired. Please sign in again.'));
      
    } finally {
      isRefreshing = false;
    }
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  requestAccess: (data) => api.post('/auth/request-access', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  listUsers: () => api.get('/auth/users'),
  updateRole: (id, role) => api.put(`/auth/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
  summary: () => api.get('/dashboard/summary'),
  opportunityCost: () => api.get('/dashboard/opportunity-cost-breakdown'),
  revenueTrend: (months = 6) => api.get(`/dashboard/revenue-trend?months=${months}`),
  settings: () => api.get('/dashboard/settings'),
  updateSettings: (data) => api.post('/dashboard/settings', data),
  basePrices: () => api.get('/dashboard/base-prices'),
  updateBasePrices: (prices) => api.post('/dashboard/base-prices', { prices }),
};

// Units
export const unitsAPI = {
  list: () => api.get('/units'),
  simple: () => api.get('/units/simple'),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
  bulkCreate: (units) => api.post('/units/bulk', units),
  bulkUpdateStatus: (unitIds, status) => api.post('/units/bulk-status', { unit_ids: unitIds, status }),
  bulkDelete: (unitIds) => api.post('/units/bulk-delete', { unit_ids: unitIds }),
};

// Leases
export const leasesAPI = {
  list: (params = {}) => api.get('/leases', { params }),
  create: (data) => api.post('/leases', data),
  update: (id, data) => api.put(`/leases/${id}`, data),
  delete: (id) => api.delete(`/leases/${id}`),
  renew: (id) => api.post(`/leases/${id}/renew`),
  generateInvoices: (id) => api.post(`/leases/${id}/generate-invoices`),
  getServices: (id) => api.get(`/leases/${id}/services`),
  updateServices: (id, data) => api.put(`/leases/${id}/services`, data),
  importServices: (id, data) => api.post(`/leases/${id}/import-services`, data),
  generateInvoiceFromServices: (id, billingPeriod) => api.post(`/invoices/generate-from-lease/${id}`, null, { params: { billing_period: billingPeriod } }),
};

// Commercial Agreements
export const agreementsAPI = {
  list: () => api.get('/agreements'),
  get: (id) => api.get(`/agreements/${id}`),
  create: (data) => api.post('/agreements', data),
  update: (id, data) => api.put(`/agreements/${id}`, data),
  delete: (id) => api.delete(`/agreements/${id}`),
};

// Clients
export const clientsAPI = {
  list: (search = '') => api.get(`/clients?search=${search}`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  deletePermanently: (id) => api.delete(`/clients/${id}/permanent`),
  reactivate: (id) => api.put(`/clients/${id}/reactivate`),
};

// Invoices (Sage 300 Compatible)
export const invoicesAPI = {
  list: (params = {}) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  bulkDelete: (invoice_ids) => api.post('/invoices/bulk-delete', { invoice_ids }),
  approve: (id) => api.post(`/invoices/${id}/approve`),
  regenerate: (id) => api.post(`/invoices/${id}/regenerate`),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  downloadCSV: (id) => api.get(`/invoices/${id}/csv`, { responseType: 'blob' }),
  sendToAccountant: (id) => api.post(`/invoices/${id}/send-to-accountant`),
};

// Documents
export const documentsAPI = {
  list: (params = {}) => api.get('/documents', { params }),
  monthlyBundles: () => api.get('/documents/monthly-bundles'),
  downloadBundle: (billingMonth) => api.get(`/documents/monthly-bundles/${billingMonth}/download`, { responseType: 'blob' }),
  rebuildBundle: (billingMonth) => api.post(`/documents/monthly-bundles/${billingMonth}/rebuild`),
  upload: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  get: (id) => api.get(`/documents/${id}`),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Notifications
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  checkExpiry: () => api.post('/notifications/check-expiry'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  unreadCount: () => api.get('/notifications/unread-count'),
};

// AI
export const aiAPI = {
  analyze: (formData) => api.post('/ai/analyze', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000 // 3 minutes for AI analysis
  }),
  extractLeaseServices: (formData) => api.post('/ai/extract-lease-services', formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000 // 3 minutes for AI analysis
  }),
};

// Service Categories
export const serviceAPI = {
  getCategories: () => api.get('/service-categories'),
};

// Admin
export const adminAPI = {
  whitelist: () => api.get('/whitelist'),
  addWhitelist: (data) => api.post('/whitelist', data),
  removeWhitelist: (id) => api.delete(`/whitelist/${id}`),
  auditLog: () => api.get('/audit-log'),
  emailLog: () => api.get('/email-log'),
  accessRequests: () => api.get('/access-requests'),
  pendingAccessCount: () => api.get('/access-requests/pending-count'),
  approveAccess: (id) => api.post(`/access-requests/${id}/approve`),
  rejectAccess: (id, note = '') => api.post(`/access-requests/${id}/reject`, { note }),
  schedulerStatus: () => api.get('/admin/scheduler-status'),
  triggerMonthlyInvoices: () => api.post('/admin/trigger-monthly-invoices'),
  syncFloorUnits: () => api.post('/admin/sync-floor-units'),
};

// Data Sync
export const dataSyncAPI = {
  getEnvironment: () => api.get('/data-sync/environment'),
  exportPreview: () => api.get('/data-sync/export-preview'),
  exportData: () => api.get('/data-sync/export', { responseType: 'blob' }),
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-sync/import-preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importData: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-sync/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  runMigrations: () => api.post('/data-sync/run-migrations'),
  runSafeMigrations: () => api.post('/data-sync/safe-migrations'),
};

export default api;
